import { decodeHTML } from "entities";

import {
  ContentElementData,
  ContentElementPdfTemplates,
} from "@courses-pm2/api/src/ts/models";
import { resolveMediaPath } from "@courses-pm2/api/src/ts/utils";

import { getChildrenByTagName } from "@courses-pm2/api/src/ts/parser/getChildrenByTagName";
import { getMetaValue } from "@courses-pm2/api/src/ts/parser/getMetaValue";
import { urls } from "../config";

const NODES_WITH_AUDIO = new Set(["complexcontent", "richtext", "simpletext"]);

/**
 * Helper that extracts basic configurations of a content element.
 */
export function parseContentElement(
  node: Element,
): Pick<ContentElementData, Exclude<keyof ContentElementData, "portalId">> {
  // Extract basic settings
  const isStandalone = node.getAttribute("isStandAlone") === "true";

  // Extract styling innfo
  const ribbon = isStandalone
    ? node.getAttribute("iconLetter") || undefined
    : undefined;

  const theme = isStandalone ? getMetaValue(node, "ElementType") : undefined;

  const speechFileUrl = (() => {
    if (!NODES_WITH_AUDIO.has(node.tagName.toLowerCase())) {
      return undefined;
    }

    const twentyThreeVideoNode = node.querySelector("TwentyThreeAudio");

    const twentyThreeSrc =
      twentyThreeVideoNode?.querySelector("DownloadPath")?.textContent;

    if (twentyThreeSrc) {
      return `${urls.video23Audio}${twentyThreeSrc}`;
    } else {
      // @todo(Lasse): Remove legacy reference to AWS Audio once fully
      // deprecated
      return node.getAttribute("audioReferenceId") || undefined;
    }
  })();

  return {
    toolbar: parseToolbar(node),
    isStandalone,
    isScrambled: parseInt(node.getAttribute("access") ?? "0", 10) > 0,
    learningObject: parseLearningObject(node),
    ribbon,
    theme,
    pdf: parsePdfConfig(node),
    speechFile: speechFileUrl || undefined,
  };
}

/**
 * Helper that extracts toolbar configurations for a content element.
 */
function parseToolbar(node: Element): ContentElementData["toolbar"] {
  return {
    enableFavorite: node.getAttribute("showFavorite") === "true",
    enableLightbox:
      node.getAttribute("showOpenInReader") === "true" ||
      node.getAttribute("openInLightbox") === "true",
    enableNewWindow:
      node.getAttribute("showOpenInNewWindow") === "true" ||
      node.getAttribute("enableOpenInNewWindow") === "true",
    enableNotebook: node.getAttribute("showNotebook") === "true",
    enablePdf: node.getAttribute("showOpenAsPdf") === "true",
    enableSpeech:
      node.getAttribute("showSpeech") === "true" ||
      !!node.getAttribute("audioReferenceId"),
  };
}

/**
 * Helper that extracts learning object data about the content element.
 */
function parseLearningObject(
  node: Element,
): ContentElementData["learningObject"] {
  // Bail out if the element isn't marked as standalone or doesn't have an id
  // associated with it
  const id = node.getAttribute("id");

  if (id === null || node.getAttribute("isStandAlone") !== "true") {
    return undefined;
  }

  // ... Otherwise return the learning object
  return {
    id,
    title: parseLearningObjectTitle(node),
    type: parseLearningObjectType(node),
  };
}

/**
 * Helper that'll parse the learning object title from the element, possibly
 * falling back to an extract of the actual content if no specific title was
 * applied.
 */
// tslint:disable-next-line cyclomatic-complexity
function parseLearningObjectTitle(node: Element): string {
  // Extract specific title if any was given
  const learningObjectTitle = node.getAttribute("learningTitle");

  if (learningObjectTitle) {
    return learningObjectTitle;
  }

  // ... Otherwise fall back to attempting to extract title by content
  // type
  switch (node.tagName.toLowerCase()) {
    case "audio":
    case "component":
    case "ibook":
    case "ibook3":
    case "ibook4":
    case "taskenginelink":
    case "image":
    case "video":
      // Extract caption data for media elements
      const title = getMetaValue(node, "Title");
      const description = getMetaValue(node, "Description");
      const caption = getMetaValue(node, "Caption");
      const authorOrArtist = getMetaValue(node, "Author");
      const creditLine = getMetaValue(node, "CreditLine");

      if (title) {
        return title;
      }

      if (description) {
        const cropAt = description.indexOf(" ", 35);

        return cropAt > -1
          ? `${description.substr(0, cropAt)}...`
          : description;
      }

      if (caption) {
        return caption;
      }

      if (authorOrArtist) {
        return authorOrArtist;
      }

      if (creditLine) {
        return creditLine;
      }
      break;

    case "complexcontent":
    case "simpletext":
    case "richtext":
      const text = decodeHTML(
        (<Element>node.querySelector("Value")).innerHTML.replace(
          /\<[^\>]+\>/g,
          "",
        ),
      );

      // Use the first ~35 characters of the text content for text-based
      if (text) {
        const cropAt = text.indexOf(" ", 35);

        return cropAt > -1 ? `${text.substr(0, cropAt)}...` : text;
      }
      break;

    case "slider":
      // Use the caption of the first element in a slider
      for (const slide of getChildrenByTagName(node, "SliderElement")) {
        const slideCaption = (slide.getAttribute("caption") || "").trim();

        if (slideCaption) {
          return slideCaption;
        }
      }
      break;

    case "accordion":
      // Use the title of the first panel in an accordion
      for (const panel of getChildrenByTagName(node, "AccordionChild")) {
        const panelTitle = (panel.getAttribute("title") || "").trim();

        if (panelTitle) {
          return decodeURIComponent(panelTitle).trim();
        }
      }
      break;

    default:
    // We don't know how to extract content from other node types...
  }

  // If we still didn't manage to extract a title, then fall back to the
  // id of the learning object!
  return node.getAttribute("id") || "";
}

/**
 * Helper that'll determine the type of the learning object based on the content
 * being displayed.
 */
function parseLearningObjectType(node: Element): string {
  const tagName = node.tagName.toLowerCase();

  switch (tagName) {
    case "audio":
    case "component":
    case "image":
    case "slider":
    case "video":
      return tagName;

    case "ibook":
    case "ibook3":
      return "i-bog";

    case "ibook4":
      return "i-bog4";

    case "taskenginelink":
      return "taskPlayer";

    case "emptyimage":
      return "image";

    case "complexcontent":
    case "richtext":
      return "richtext";

    case "simpletext":
      return "text";

    default:
      return "unknown";
  }
}

/**
 * Helper that extracts configurations for pdf ecport of the content element.
 */
function parsePdfConfig(node: Element): ContentElementData["pdf"] {
  // Determine the template type
  // Defines the kind of PDF template/format that should be rendered.
  let template: ContentElementPdfTemplates = ContentElementPdfTemplates.BOOK;

  switch ((node.getAttribute("format") || "").toLowerCase()) {
    case "article":
      template = ContentElementPdfTemplates.ARTICLE;
      break;

    case "book":
      template = ContentElementPdfTemplates.BOOK;
      break;

    case "booksprog":
      template = ContentElementPdfTemplates.BOOK_LANGUAGE;

      break;
    case "sourcetext":
      template = ContentElementPdfTemplates.SOURCE;
      break;

    case "munksgaard":
      template = ContentElementPdfTemplates.MUNKSGAARD;
      break;

    case "hansreitzel":
      template = ContentElementPdfTemplates.HANS_REITZEL;
      break;

    default:
    // We don't know how to handle any other kind of templates
  }

  return {
    file: resolveMediaPath(node.getAttribute("pdfFile")),
    headerImage: resolveMediaPath(node.getAttribute("pdfHeaderImage")),
    lineCount: node.getAttribute("lineCounter") === "true",
    template,
  };
}
